import moment from "moment-timezone";

/**
 * Хелперы для маппинга значений фильтра из querystring в аргументы запроса.
 *
 * Значения приходят из адресной строки (`FilterProvider` -> `qs.parse`), то
 * есть это произвольные строки: пользователь может поправить URL руками, а
 * старая ссылка может нести ключи от прошлой версии фильтра. Поэтому:
 *
 * - enum'ы сверяются с `Object.values(...)` сгенерированных типов, мусор
 *   выбрасывается (иначе GraphQL уронит весь запрос ошибкой валидации
 *   переменных, а список молча окажется пустым);
 * - даты парсятся moment'ом и выбрасываются, если невалидны: у
 *   `new Date(x).toISOString()` на невалидной дате RangeError прилетает
 *   прямо в рендере (белый экран, ErrorBoundary в кабинете нет).
 *
 * Приём и формулировки взяты из `features/purchases/model/buildPurchasesFilter.ts`
 * (Task 3 того же плана), здесь вынесены в общее место, потому что тот же
 * маппинг нужен платежам, подпискам и спискам возвратов/споров.
 */
export function isEnumValue<T extends Record<string, string>>(
  enumObject: T,
  value: unknown
): value is T[keyof T] {
  return (
    typeof value === "string" &&
    (Object.values(enumObject) as string[]).includes(value)
  );
}

export function toIsoDate(value: unknown): string | undefined {
  if (typeof value !== "string" || !value) return undefined;

  // `+` в смещении часового пояса ("...T00:00:00+03:00") при декодировании
  // querystring может превратиться в пробел: чиним, прежде чем парсить.
  const normalized = value.replace(/\s(\d{2}:\d{2})$/, "+$1");
  const parsed = moment(normalized, moment.ISO_8601);

  return parsed.isValid() ? parsed.toISOString() : undefined;
}

/**
 * Список id из querystring. Форма значения зависит от того, как его отдал
 * `qs.parse`, поэтому принимаем все три:
 *
 * - массив (`?filter[productIds][]=a&filter[productIds][]=b`) — обычный случай;
 * - объект `{ "0": "a", "1": "b", ... }` — так `qs` отдаёт список длиннее
 *   `arrayLimit` (в `FilterProvider` лимит поднят, но ссылки из прошлых
 *   версий и чужие клиенты `qs` могут прислать и объектную форму);
 * - одиночную строку (`?filter[productIds]=a`) — рукописная ссылка.
 */
export function toIdList(value: unknown): string[] | undefined {
  const raw = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? [value]
      : value && typeof value === "object"
        ? Object.values(value)
        : [];

  const ids = raw.filter(
    (item): item is string => typeof item === "string" && item.length > 0
  );

  return ids.length > 0 ? ids : undefined;
}
